// Quick chips (map-app pattern): toggles in a horizontal carousel under the search bar (phones) or beside it (wider screens).
// Filter chips edit the shared filter state, the pin switch flips the pin extra value; the menu controls stay in sync (app-settings.mjs).
import {t, number} from './i18n.mjs';
import {icon} from './icons.mjs';
import {POWER_FROM, POWER_TO, chipState, toggleChip} from './chip-filters.mjs';

const esc = value => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const arrow = (id, name) => `<button id="chips-${id}" class="qchip-arrow" type="button" tabindex="-1" aria-hidden="true">${icon(name)}</button>`;
const filterChip = (id, label) => `<button class="qchip" type="button" data-chip="${id}" aria-pressed="false">${esc(label)}</button>`;
export const chipLabels = {from: () => t('chipFrom', {n: number(POWER_FROM)}), to: () => t('chipTo', {n: number(POWER_TO)}), free: () => t('chipFree')};
// Pin switch: a well with a thumb under the active unit; both units (kW at the start, € at the end) are always printed. Language-neutral, so no catalog text.
const pinSwitch = `<button id="chip-pin" class="qchip-switch" type="button" role="switch" aria-checked="false" data-mode="kw" data-i18n-aria="chipPinSwitch" aria-label="${esc(t('chipPinSwitch'))}" data-i18n-title="chipPinTitle" title="${esc(t('chipPinTitle'))}"><span class="qsw-knob" aria-hidden="true"></span><span class="qsw-unit qsw-kw" aria-hidden="true">kW</span><span class="qsw-unit qsw-eur" aria-hidden="true">€</span></button>`;
export const chipsMarkup = () => `<div id="quick-chips" class="quick-chips" role="group" data-i18n-aria="chipsLabel" aria-label="${t('chipsLabel')}">${arrow('prev', 'prev')}
    <div id="chips-scroll" class="quick-scroll">${pinSwitch}${['from', 'to', 'free'].map(id => filterChip(id, chipLabels[id]())).join('')}</div>${arrow('next', 'next')}</div>`;

// A tap on the switch: kW and price alternate; from Off the first tap turns the pins on with kW (the default value, needs no price data), the next tap reaches price.
export const nextPinMode = mode => mode === 'kw' ? 'price' : 'kw';

// The switch shows the pin value: thumb under "€" (end) = price (checked); thumb under "kW" (start) = kW; Off = no thumb, both units muted.
export function syncPinChip(mode, doc = document) {
  const chip = doc.getElementById('chip-pin');
  if (!chip) return;
  const key = mode === 'off' ? 'chipPinOn' : 'chipPinSwitch';
  chip.dataset.mode = mode; chip.setAttribute('aria-checked', String(mode === 'price'));
  chip.dataset.i18nAria = key; chip.setAttribute('aria-label', t(key));
}

// Filter chips: pressed state and labels follow the filter state; `apply` runs after a tap (refresh the menu controls, map, badge, URL).
export function chipControls(filters, {apply, doc = document}) {
  const nodes = [...doc.querySelectorAll('[data-chip]')];
  const sync = () => {
    const state = chipState(filters);
    for (const node of nodes) { node.setAttribute('aria-pressed', String(state[node.dataset.chip])); node.textContent = chipLabels[node.dataset.chip](); }
  };
  for (const node of nodes) node.onclick = () => { toggleChip(filters, node.dataset.chip); sync(); apply(); };
  sync();
  return sync;
}

// Carousel: overflow state for the edge fade and the pointer arrows (desktop); the arrows page by most of the visible width.
export function chipsCarousel({doc = document, reducedMotion = () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches} = {}) {
  const root = doc.getElementById('quick-chips'), scroll = doc.getElementById('chips-scroll');
  if (!root || !scroll) return null;
  const update = () => {
    const max = scroll.scrollWidth - scroll.clientWidth;
    root.toggleAttribute('data-more-start', scroll.scrollLeft > 1);
    root.toggleAttribute('data-more-end', scroll.scrollLeft < max - 1);
  };
  const page = direction => scroll.scrollBy({left: direction * Math.max(80, scroll.clientWidth * .75), behavior: reducedMotion() ? 'auto' : 'smooth'});
  doc.getElementById('chips-prev').addEventListener('click', () => page(-1));
  doc.getElementById('chips-next').addEventListener('click', () => page(1));
  scroll.addEventListener('scroll', update, {passive: true});
  if (globalThis.ResizeObserver) { const watch = new ResizeObserver(update); watch.observe(scroll); for (const chip of scroll.children) watch.observe(chip); }
  update();
  return {update, page};
}


// Beside the search bar the row needs room for the switch and at least three whole chips; otherwise it stays under the bar like on phones.
// Measured, not guessed: the row is laid out beside the bar and its real chips are checked against the space, so every language and font decides for itself.
export const CHIPS_MIN_VISIBLE = 4, CHIPS_FADE = 28;
export function chipsFit(scroll, {minVisible = CHIPS_MIN_VISIBLE, fade = CHIPS_FADE, padding = parseFloat(globalThis.getComputedStyle?.(scroll).paddingRight) || 0} = {}) {
  const chips = [...scroll.children], limit = scroll.getBoundingClientRect().right - padding;
  const rights = chips.map(chip => chip.getBoundingClientRect().right);
  if (!chips.length || rights.at(-1) <= limit + .5) return chips.length >= Math.min(minVisible, chips.length);
  return rights.filter(right => right <= limit - fade + .5).length >= minVisible;
}
export function chipsPlacement({doc = document, win = globalThis} = {}) {
  const root = doc.documentElement, scroll = doc.getElementById('chips-scroll');
  if (!scroll) return null;
  // Try the beside layout, keep it only when the real chips fit (same tick, so no frame shows the trial).
  const update = () => {
    root.setAttribute('data-chips-beside', '');
    const beside = chipsFit(scroll);
    root.toggleAttribute('data-chips-beside', beside);
    return beside;
  };
  if (win.ResizeObserver) { const watch = new win.ResizeObserver(update); watch.observe(root); for (const chip of scroll.children) watch.observe(chip); } else win.addEventListener?.('resize', update);
  doc.fonts?.ready?.then(update);
  update();
  return {update};
}
